/**
 * Los identificadores de las transiciones compartidas entre pantallas.
 *
 * ⚠ **Están aquí y no escritos a mano en cada sitio por un motivo concreto: si los
 * dos extremos dejan de coincidir, NADA falla.** Un `layoutId` desparejado no lanza,
 * no avisa y no reprueba ninguna compuerta — la transición sencillamente no ocurre,
 * y el elemento aparece de golpe donde antes viajaba. Es de los pocos defectos que
 * sólo se ven mirando la pantalla, así que la constante es la que garantiza que
 * renombrar uno renombre los dos.
 */

/**
 * La lámina de cristal que en el acceso sostiene el formulario y, al entrar, se
 * estrecha y se estira hasta convertirse en la barra lateral.
 *
 * Lo llevan `paginas/Login.tsx` y `componentes/layout/BarraLateral.tsx`.
 */
export const LAMINA_SESION = "lamina-sesion"

/**
 * El logotipo de SETIECA: termina la secuencia del acceso en el centro de la
 * lámina y acaba arriba del todo en la barra lateral.
 *
 * Lo llevan `componentes/marca/IntroMarca.tsx` y `componentes/layout/BarraLateral.tsx`.
 */
export const MARCA_SESION = "marca-sesion"

/**
 * Cómo viaja la lámina.
 *
 * ⚠ **Un `tween` con curva de salida, NO un muelle.** Con `spring` se veía
 * golpeada: un muelle acelera al principio y frena de golpe al final, y eso en un
 * elemento del tamaño de la pantalla se percibe como un tirón, no como algo que se
 * transforma. La curva de aquí sale rápido y se posa — el mismo `--curva-entrada`
 * que usa el resto del sistema, sólo que estirado, porque aquí no se mueve un panel
 * de 200 px sino media pantalla.
 */
export const VIAJE_LAMINA = {
  duration: 0.62,
  ease: [0.16, 1, 0.3, 1],
} as const

/**
 * El guion de la entrada al sistema, en segundos.
 *
 * ⚠ **El orden importa y estaba al revés.** Aparecía todo a la vez —encabezado,
 * contenido y menú— mientras la lámina todavía viajaba, así que lo que se veía era
 * el sistema ya montado y una tarjeta llegando tarde a un sitio ya ocupado. Primero
 * se forma el menú, y sólo cuando está en su sitio se despliegan el encabezado y el
 * contenido.
 *
 * Se declaran aquí y no en cada componente porque son un solo compás repartido
 * entre tres archivos: `AppShell` pone el encabezado y el contenido, `BarraLateral`
 * el suyo, y basta con que uno se desajuste para que el escalonado deje de leerse.
 */
export const ENTRADA = {
  /** El contenido del menú, en cuanto la lámina casi ha llegado. */
  menu: { delay: 0.4, duration: 0.35 },
  /** El encabezado, después. */
  encabezado: { delay: 0.55, duration: 0.35 },
  /** Y por último el área de trabajo, que es lo que más pesa en pantalla. */
  contenido: { delay: 0.68, duration: 0.4 },
} as const

/**
 * Lo que tarda el acceso en apagarse antes de navegar.
 *
 * ⚠ **Se desvanece el CONTENIDO de la tarjeta, no la tarjeta.** La lámina tiene que
 * seguir ahí para poder viajar; lo que sobra durante el viaje es el formulario y la
 * marca grande. Sin esta pausa, el contenido desaparece en el mismo fotograma en que
 * empieza el movimiento y el efecto es un corte.
 */
export const SALIDA_ACCESO = 0.22
